import { ChangeEventHandler, useState } from "react";
import { useTranslation } from "react-i18next";
import produce from "immer";
import { Box, TextField, Typography } from "@material-ui/core";
import { useRuleContext } from "pages/Rules/Form/context/ruleContext";

import SwitchField from "components/Forms/SwitchField";
import { DoneNotification } from "@dashboard-v3/api";
import { NOTIFICATION_LIMITS } from "@dashboard-v3/shared";
import { Field, FormFields } from "../../../../common/FormFields";
import { ErrorMessage } from "../../Actions/Styled";
import useNotificationVariables from "pages/Rules/hooks/useNotificationVariables";
import AutocompleteSearchInput from "components/AutocompleteSearchInput";
import { getApprovalSteps } from "pages/Rules/Form/context/ruleSelectors";

export default function DoneNotificationSettings() {
  const { t } = useTranslation("rules");
  const { state, dispatch } = useRuleContext();
  const { doneNotification } = getApprovalSteps(state.rule);
  const [isEnabled, setIsEnabled] = useState(Boolean(doneNotification));
  const { variables, replaceIdsWithLabels, replaceLabelsWithIds } =
    useNotificationVariables({
      ruleType: "SEND_DELAY",
      notificationType: "DONE",
    });
  const titleError = hasError(
    "COMPLETION_TITLE_REQUIRED",
    "COMPLETION_TITLE_MAX_LENGTH"
  );
  const approvedBodyError = hasError(
    "COMPLETION_APPROVEDBODYTEXT_REQUIRED",
    "COMPLETION_APPROVEDBODYTEXT_MAX_LENGTH"
  );
  const rejectedBodyError = hasError(
    "COMPLETION_REJECTEDBODYTEXT_REQUIRED",
    "COMPLETION_REJECTEDBODYTEXT_MAX_LENGTH"
  );
  const canceledBodyError = hasError(
    "COMPLETION_CANCELEDBODYTEXT_REQUIRED",
    "COMPLETION_CANCELEDBODYTEXT_MAX_LENGTH"
  );

  const updateSettings = (update: Partial<DoneNotification>) => {
    dispatch({
      type: "updateSendDelay",
      payload: { doneNotification: update },
    });
  };

  const onTitleChange: ChangeEventHandler<HTMLInputElement> = e => {
    const { name, value } = e.target;

    const update = produce(doneNotification, draft => {
      draft[name] = value;
    });

    updateSettings(update);
  };

  function onBodyChange(name: string, value: string) {
    const update = produce(doneNotification, draft => {
      draft[name] = replaceLabelsWithIds(value);
    });

    updateSettings(update);
  }

  const onEnableNotification: ChangeEventHandler<HTMLInputElement> = e => {
    const { checked } = e.target;
    setIsEnabled(checked);

    if (!checked) return updateSettings(null);

    updateSettings({
      title: t("sendDelay.doneNotification.title"),
      approvedBodyText: t("sendDelay.doneNotification.approvedBodyTxt"),
      rejectedBodyText: t("sendDelay.doneNotification.rejectedBodyTxt"),
      canceledBodyText: t("sendDelay.doneNotification.canceledBodyTxt"),
    });
  };

  function hasError(...errorCodes: string[]) {
    const errors = (state.steps.sendDelay?.error || []) as string[];

    return errors.some(error => errorCodes.includes(error));
  }

  return (
    <Box marginBottom="5px" marginTop="5px">
      <SwitchField
        style={{ marginBottom: isEnabled ? 10 : 0 }}
        label={t("sendDelay.doneNotification.enableLabel")}
        description={
          !isEnabled && t("sendDelay.doneNotification.enableDescription")
        }
        value={isEnabled}
        onChange={onEnableNotification}
      />
      {isEnabled && (
        <div style={{ padding: "8px 16px 16px" }}>
          <FormFields>
            <Field>
              <TextField
                name="title"
                variant="outlined"
                label={t("approvalSteps.doneNotification.title")}
                value={doneNotification.title}
                onChange={onTitleChange}
                error={titleError}
                inputProps={{ maxLength: NOTIFICATION_LIMITS.TITLE }}
              />
              <ErrorMessage
                isError={titleError}
                wording={t("approvalSteps.fieldRequired")}
              />
            </Field>

            <Field>
              <Typography
                variant="caption"
                color={approvedBodyError ? "error" : "textSecondary"}
              >
                {t("approvalSteps.doneNotification.approvalBody")}
              </Typography>
              <AutocompleteSearchInput
                name="approvedBodyText"
                multiple
                rows={5}
                fullWidth
                variables={variables}
                error={approvedBodyError}
                maxLength={NOTIFICATION_LIMITS.BODY_TEXT}
                value={replaceIdsWithLabels(doneNotification.approvedBodyText)}
                onChange={value => onBodyChange("approvedBodyText", value)}
              />
              <ErrorMessage
                isError={approvedBodyError}
                wording={t("approvalSteps.fieldRequired")}
              />
            </Field>

            <Field>
              <Typography
                variant="caption"
                color={rejectedBodyError ? "error" : "textSecondary"}
              >
                {t("approvalSteps.doneNotification.rejectBody")}
              </Typography>
              <AutocompleteSearchInput
                name="rejectedBodyText"
                multiple
                rows={5}
                fullWidth
                variables={variables}
                error={rejectedBodyError}
                maxLength={NOTIFICATION_LIMITS.BODY_TEXT}
                value={replaceIdsWithLabels(doneNotification.rejectedBodyText)}
                onChange={value => onBodyChange("rejectedBodyText", value)}
              />
              <ErrorMessage
                isError={rejectedBodyError}
                wording={t("approvalSteps.fieldRequired")}
              />
            </Field>

            <Field>
              <Typography
                variant="caption"
                color={canceledBodyError ? "error" : "textSecondary"}
              >
                {t("approvalSteps.doneNotification.canceledBody")}
              </Typography>
              <AutocompleteSearchInput
                name="canceledBodyText"
                multiple
                rows={5}
                fullWidth
                variables={variables}
                error={canceledBodyError}
                maxLength={NOTIFICATION_LIMITS.BODY_TEXT}
                value={replaceIdsWithLabels(doneNotification.canceledBodyText)}
                onChange={value => onBodyChange("canceledBodyText", value)}
              />
              <ErrorMessage
                isError={canceledBodyError}
                wording={t("approvalSteps.fieldRequired")}
              />
            </Field>
          </FormFields>
        </div>
      )}
    </Box>
  );
}
